import React from 'react';
import { useSelector } from 'react-redux';
import { getSiteId } from 'services/app';
import { getIsEpicGamesOauth, getPrimaryToken, getUserService, getUserUid } from 'services/auth';
import { DropdownOption, GameType, IFortniteLeaderboardBlockData, LeaderboardType } from '../../utils';
import { getSeasons, getSessions, getTournaments, getUserLeaderboardPosition } from './api';
import { IFortniteLeaderboardItem } from 'models/IFortniteLeaderboard';

const useFilters = (defaultFilters: IFortniteLeaderboardBlockData['defaultFilters']) => {
  const [leaderboardType, setLeaderboardType] = React.useState(defaultFilters.leaderboardType);
  const [gameType, setGameType] = React.useState(defaultFilters.gameType);
  const [season, setSeason] = React.useState(defaultFilters.season);
  const [tournament, setTournament] = React.useState(defaultFilters.tournament);
  const [session, setSession] = React.useState(defaultFilters.session);

  const [tournaments, setTournaments] = React.useState<DropdownOption[]>([]);
  const [sessions, setSessions] = React.useState<DropdownOption[]>([]);
  const [seasons, setSeasons] = React.useState<DropdownOption[]>([]);

  const [loggedUserLeaderboardPosition, setLoggedUserLeaderboardPosition] = React.useState<IFortniteLeaderboardItem | null>(null);

  const primaryToken = useSelector(getPrimaryToken) || '';
  const siteId = useSelector(getSiteId);
  const isEpicGamesOauth = useSelector(getIsEpicGamesOauth);

  const isRanked = leaderboardType === 'ranked';

  const onChangeSeason = async (value: string) => {
    if (!value || season === value) {
      return;
    }

    setSeason(value);
    if (tournament) {
      setTournament('');
    }
    if (session) {
      setSession('');
    }
    if (tournaments.length > 0) {
      setTournaments([]);
    }
    if (sessions.length > 0) {
      setSessions([]);
    }

    if (isRanked) {
      await loadLoggedUserLeaderboardPosition({ targetSeason: value });
      return;
    }

    const result = await getTournaments({
      season: value,
      gameType,
      primaryToken,
      siteId,
    });

    setTournaments(result);
  };

  const onChangeGameType = async (newGameType: GameType) => {
    if (gameType === newGameType) {
      return;
    }

    setGameType(newGameType);
    if (tournament) {
      setTournament('');
    }
    if (session) {
      setSession('');
    }
    if (seasons.length > 0) {
      setSeasons([]);
    }
    if (sessions.length > 0) {
      setSessions([]);
    }
    if (tournaments.length > 0) {
      setTournaments([]);
    }
    if (season) {
      setSeason('');
    }
    if (loggedUserLeaderboardPosition) {
      setLoggedUserLeaderboardPosition(null);
    }

    const result = await getSeasons({
      gameType: newGameType,
      leaderboardType,
      primaryToken,
      siteId,
    });

    setSeasons(result);
  };

  const onChangeLeaderboardType = async (newLeaderboardType: LeaderboardType) => {
    if (leaderboardType === newLeaderboardType) {
      return;
    }

    setLeaderboardType(newLeaderboardType);
    if (season) {
      setSeason('');
    }

    if (tournament) {
      setTournament('');
    }

    if (session) {
      setSession('');
    }

    if (seasons.length > 0) {
      setSeasons([]);
    }

    if (tournaments.length > 0) {
      setTournaments([]);
    }

    if (sessions.length > 0) {
      setSessions([]);
    }

    if (loggedUserLeaderboardPosition) {
      setLoggedUserLeaderboardPosition(null);
    }

    const result = await getSeasons({
      gameType,
      primaryToken,
      leaderboardType: newLeaderboardType,
      siteId,
    });

    setSeasons(result);
  };

  const onChangeTournament = async (newTournament: string) => {
    if (!newTournament || tournament === newTournament || isRanked) {
      return;
    }

    setTournament(newTournament);
    if (session) {
      setSession('');
    }
    if (sessions.length > 0) {
      setSessions([]);
    }
    if (loggedUserLeaderboardPosition) {
      setLoggedUserLeaderboardPosition(null);
    }

    const result = await getSessions({
      eventId: newTournament,
      primaryToken,
      siteId,
    });
    setSessions(result);
  };

  const onChangeSession = async (newSession: string) => {
    if (!newSession || session === newSession || isRanked) {
      return;
    }

    setSession(newSession);
    await loadLoggedUserLeaderboardPosition({ targetSeason: season, targetSession: newSession });
  };

  const loadLoggedUserLeaderboardPosition = async ({ targetSession, targetSeason }: { targetSeason: string; targetSession?: string; }) => {
    if (!isEpicGamesOauth) {
      if (loggedUserLeaderboardPosition) {
        setLoggedUserLeaderboardPosition(null);
      }
      return;
    }

    const result = await getUserLeaderboardPosition({
      eventId: getEventId(targetSeason),
      gameType,
      leaderboardType,
      primaryToken,
      siteId,
      ...targetSession && {
        eventWindowId: getEventWindowId(targetSession),
      },
    });

    setLoggedUserLeaderboardPosition(result);
  };

  React.useEffect(() => {
    if (isRanked) {

      if (season) {
        loadLoggedUserLeaderboardPosition({ targetSeason: season });
      }

    } else {

      if (season && tournament && session) {
        loadLoggedUserLeaderboardPosition({ targetSeason: season, targetSession: session });
      }

    }
  }, []);

  React.useEffect(() => {
    let isMounted = true;

    getSeasons({
      gameType,
      primaryToken,
      siteId,
      leaderboardType,
    }).then((result) => {
      if (isMounted) {
        setSeasons(result);
      }
    });

    if (!isRanked) {
      if (season && leaderboardType) {
        getTournaments({
          season,
          gameType,
          primaryToken,
          siteId,
        }).then((result) => {
          if (isMounted) {
            setTournaments(result);
          }
        });
      }

      if (tournament) {
        getSessions({
          eventId: tournament,
          primaryToken,
          siteId,
        }).then((result) => {
          if (isMounted) {
            setSessions(result);
          }
        });
      }
    }

    return () => {
      isMounted = false;
    };
  }, []);

  React.useEffect(() => {
    return () => {
      if (loggedUserLeaderboardPosition) {
        setLoggedUserLeaderboardPosition(null);
      }
    };
  }, [loggedUserLeaderboardPosition]);

  const getEventId = React.useCallback((targetSeason: string) => {
    if (isRanked) {
      const rankingType = gameType === 'battle-royale' ? 'br' : 'zb';
      return `epicgames_ranked_${rankingType}_season${targetSeason}`;
    }

    return tournament;
  }, [isRanked, gameType, tournament]);

  const getEventWindowId = React.useCallback((targetSession: string) => {
    if (isRanked) {
      // Division 17 is the highest division in the game, and we only show them in the ranked leaderboard
      return 'division_17';
    }

    return targetSession;
  }, [isRanked]);

  return {
    seasons: {
      options: seasons,
      selected: seasons.find(s => s.value === season),
      onChange: onChangeSeason,
    },
    tournaments: {
      options: tournaments,
      selected: tournaments.find(t => t.value === tournament),
      onChange: onChangeTournament,
    },
    sessions: {
      options: sessions,
      selected: sessions.find(s => s.value === session),
      onChange: onChangeSession,
    },
    isRanked,
    isBattleRoyale: gameType === 'battle-royale',
    onChangeGameType,
    onChangeLeaderboardType,
    eventId: getEventId(season),
    eventWindowId: getEventWindowId(session),
    loggedUserLeaderboardPosition,
  };
};

export default useFilters;
